import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import { FileText, MapPin, Phone, User, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COUNTRY_CODES = [
  { code: '+52', country: 'México', flag: 'MX' },
  { code: '+1', country: 'Estados Unidos', flag: 'US' },
  { code: '+1', country: 'Canadá', flag: 'CA' },
  { code: '+57', country: 'Colombia', flag: 'CO' },
  { code: '+54', country: 'Argentina', flag: 'AR' },
  { code: '+56', country: 'Chile', flag: 'CL' },
  { code: '+51', country: 'Perú', flag: 'PE' },
  { code: '+55', country: 'Brasil', flag: 'BR' },
  { code: '+34', country: 'España', flag: 'ES' },
  { code: '+502', country: 'Guatemala', flag: 'GT' },
  { code: '+503', country: 'El Salvador', flag: 'SV' },
  { code: '+504', country: 'Honduras', flag: 'HN' },
  { code: '+505', country: 'Nicaragua', flag: 'NI' },
  { code: '+506', country: 'Costa Rica', flag: 'CR' },
  { code: '+507', country: 'Panamá', flag: 'PA' },
];

const MEXICAN_STATES = [
  'Aguascalientes', 'Baja California', 'Baja California Sur', 'Campeche', 'Chiapas',
  'Chihuahua', 'Ciudad de México', 'Coahuila', 'Colima', 'Durango',
  'Estado de México', 'Guanajuato', 'Guerrero', 'Hidalgo', 'Jalisco',
  'Michoacán', 'Morelos', 'Nayarit', 'Nuevo León', 'Oaxaca',
  'Puebla', 'Querétaro', 'Quintana Roo', 'San Luis Potosí', 'Sinaloa',
  'Sonora', 'Tabasco', 'Tamaulipas', 'Tlaxcala', 'Veracruz', 'Yucatán', 'Zacatecas',
];

const CFDI_USES = [
  { value: 'G01', label: 'G01 - Adquisición de mercancías' },
  { value: 'G03', label: 'G03 - Gastos en general' },
  { value: 'I01', label: 'I01 - Construcciones' },
  { value: 'I08', label: 'I08 - Otra maquinaria y equipo' },
  { value: 'P01', label: 'P01 - Por definir' },
];

const SectionTitle = ({ icon: Icon, title }) => (
  <div className="flex items-center gap-2 pb-3 mb-4 border-b border-accent/20">
    <div className="w-7 h-7 rounded-md bg-accent/10 flex items-center justify-center">
      <Icon className="w-4 h-4 text-accent" />
    </div>
    <h4 className="font-bold text-primary text-sm tracking-wide uppercase">{title}</h4>
  </div>
);

const FieldError = ({ error }) => {
  if (!error) return null;
  return <p className="text-xs text-red-500 mt-1">{error}</p>;
};

const CustomerForm = ({
  billing, shipping, phone, invoiceRequested,
  onBillingChange, onShippingChange, onPhoneChange, onInvoiceRequestedChange,
  errors = {}
}) => {
  const updateBilling = (key, value) => onBillingChange({ ...billing, [key]: value });
  const updateShipping = (key, value) => onShippingChange({ ...shipping, [key]: value });
  const updatePhone = (key, value) => onPhoneChange({ ...phone, [key]: value });

  // Shipping CP lookup state
  const [shippingColonias, setShippingColonias] = useState([]);
  const [shippingCpLoading, setShippingCpLoading] = useState(false);
  const [shippingCpError, setShippingCpError] = useState('');

  // Billing CP lookup state
  const [billingColonias, setBillingColonias] = useState([]);
  const [billingCpLoading, setBillingCpLoading] = useState(false);

  const lookupPostalCode = useCallback(async (cp, target) => {
    if (!cp || cp.length !== 5) return;
    const setLoading = target === 'shipping' ? setShippingCpLoading : setBillingCpLoading;
    const setColonias = target === 'shipping' ? setShippingColonias : setBillingColonias;
    const setError = target === 'shipping' ? setShippingCpError : () => {};
    const onChange = target === 'shipping' ? onShippingChange : onBillingChange;
    const currentData = target === 'shipping' ? shipping : billing;

    setLoading(true);
    setError('');
    try {
      const res = await axios.get(`${API}/postal-codes/${cp}`);
      const { estado, municipio, colonias } = res.data;
      onChange({
        ...currentData,
        postal_code: cp,
        state: estado,
        city: municipio,
        neighborhood: colonias.length === 1 ? colonias[0] : '',
      });
      setColonias(colonias);
    } catch {
      setColonias([]);
      if (target === 'shipping') setShippingCpError('No se encontró el código postal');
    }
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- State setters are stable; shipping/billing/onChange are the meaningful deps
  }, [shipping, billing, onShippingChange, onBillingChange]);

  const handleShippingCpChange = (value) => {
    const cp = value.replace(/\D/g, '').slice(0, 5);
    updateShipping('postal_code', cp);
    if (cp.length === 5) lookupPostalCode(cp, 'shipping');
    if (cp.length < 5) { setShippingColonias([]); setShippingCpError(''); }
  };

  const handleBillingCpChange = (value) => {
    const cp = value.replace(/\D/g, '').slice(0, 5);
    updateBilling('postal_code', cp);
    if (cp.length === 5) lookupPostalCode(cp, 'billing');
    if (cp.length < 5) setBillingColonias([]);
  };

  const handleSameAsBilling = (checked) => {
    if (checked) {
      onShippingChange({
        ...shipping,
        same_as_billing: true,
        contact_name: shipping.contact_name || billing.name_or_company,
        country: billing.country,
        state: billing.state,
        street: billing.street,
        neighborhood: billing.neighborhood,
        city: billing.city,
        postal_code: billing.postal_code,
      });
    } else {
      onShippingChange({ ...shipping, same_as_billing: false });
    }
  };

  return (
    <div className="space-y-8" data-testid="customer-form">
      {/* ==================== CONTACT INFO ==================== */}
      <section data-testid="section-contact">
        <SectionTitle icon={User} title="Datos de contacto" />
        <div className="space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs font-medium text-muted-foreground">Nombre completo o empresa *</Label>
              <Input
                value={billing.name_or_company}
                onChange={(e) => updateBilling('name_or_company', e.target.value)}
                placeholder="Tu nombre o empresa"
                className="mt-1"
                name="name"
                autoComplete="name"
                data-testid="input-billing-name"
              />
              <FieldError error={errors.billing_name} />
            </div>
            <div>
              <Label className="text-xs font-medium text-muted-foreground">Correo electrónico *</Label>
              <Input
                type="email"
                value={billing.email}
                onChange={(e) => updateBilling('email', e.target.value)}
                placeholder="correo@ejemplo.com"
                className="mt-1"
                name="email"
                autoComplete="email"
                data-testid="input-billing-email"
              />
              <FieldError error={errors.billing_email} />
            </div>
          </div>
        </div>
      </section>

      {/* ==================== PHONE ==================== */}
      <section data-testid="section-phone">
        <SectionTitle icon={Phone} title="Número de contacto" />
        <div className="space-y-4">
          <div className="grid grid-cols-[140px_1fr] gap-3">
            <div>
              <Label className="text-xs font-medium text-muted-foreground">Código de país *</Label>
              <Select value={phone.country_code} onValueChange={(v) => updatePhone('country_code', v)}>
                <SelectTrigger className="mt-1" data-testid="select-phone-code">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {COUNTRY_CODES.map((c, i) => (
                    <SelectItem key={`${c.code}-${c.flag}-${i}`} value={c.code}>
                      {c.flag} {c.code} {c.country}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FieldError error={errors.phone_code} />
            </div>
            <div>
              <Label className="text-xs font-medium text-muted-foreground">Número de teléfono *</Label>
              <Input
                value={phone.number}
                onChange={(e) => updatePhone('number', e.target.value.replace(/[^\d]/g, '').slice(0, 10))}
                placeholder="10 dígitos"
                className="mt-1"
                type="tel"
                name="phone"
                autoComplete="tel-national"
                maxLength={10}
                data-testid="input-phone-number"
              />
              <FieldError error={errors.phone_number} />
            </div>
          </div>
          <p className="text-xs text-muted-foreground">
            Este número se usará para contactarte sobre tu pedido y coordinar la entrega.
          </p>
        </div>
      </section>

      {/* ==================== SHIPPING ==================== */}
      <section data-testid="section-shipping">
        <SectionTitle icon={MapPin} title="Dirección de entrega" />
        <div className="space-y-4">
          <div>
            <Label className="text-xs font-medium text-muted-foreground">Nombre de contacto *</Label>
            <Input
              value={shipping.contact_name}
              onChange={(e) => updateShipping('contact_name', e.target.value)}
              placeholder="Persona que recibe el paquete"
              className="mt-1"
              name="shipping-name"
              autoComplete="shipping name"
              data-testid="input-shipping-contact"
            />
            <FieldError error={errors.shipping_contact} />
          </div>

          <div>
            <Label className="text-xs font-medium text-muted-foreground">Empresa (opcional)</Label>
            <Input
              value={shipping.company || ''}
              onChange={(e) => updateShipping('company', e.target.value)}
              placeholder="Nombre de la empresa si aplica"
              className="mt-1"
              name="shipping-organization"
              autoComplete="shipping organization"
              data-testid="input-shipping-company"
            />
          </div>

          <div>
            <Label className="text-xs font-medium text-muted-foreground">Calle y número *</Label>
            <Input
              value={shipping.street}
              onChange={(e) => updateShipping('street', e.target.value)}
              placeholder="Ej: Av. Reforma 123"
              className="mt-1"
              name="shipping-street"
              autoComplete="shipping street-address"
              data-testid="input-shipping-street"
            />
            <FieldError error={errors.shipping_street} />
          </div>

          {/* CP first — triggers auto-fill */}
          <div className="grid sm:grid-cols-3 gap-4">
            <div>
              <Label className="text-xs font-medium text-muted-foreground">Código postal *</Label>
              <div className="relative mt-1">
                <Input
                  value={shipping.postal_code}
                  onChange={(e) => handleShippingCpChange(e.target.value)}
                  placeholder="00000"
                  maxLength={5}
                  inputMode="numeric"
                  name="shipping-postal-code"
                  autoComplete="shipping postal-code"
                  data-testid="input-shipping-postal"
                />
                {shippingCpLoading && (
                  <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-muted-foreground" />
                )}
              </div>
              <FieldError error={errors.shipping_postal} />
              {shippingCpError && <p className="text-xs text-amber-600 mt-1">{shippingCpError}</p>}
            </div>
            <div>
              <Label className="text-xs font-medium text-muted-foreground">Estado *</Label>
              {shipping.country === 'México' ? (
                <Select value={shipping.state} onValueChange={(v) => updateShipping('state', v)}>
                  <SelectTrigger className="mt-1" data-testid="select-shipping-state">
                    <SelectValue placeholder="Selecciona un estado" />
                  </SelectTrigger>
                  <SelectContent>
                    {MEXICAN_STATES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              ) : (
                <Input
                  value={shipping.state}
                  onChange={(e) => updateShipping('state', e.target.value)}
                  placeholder="Estado o provincia"
                  className="mt-1"
                />
              )}
              <FieldError error={errors.shipping_state} />
            </div>
            <div>
              <Label className="text-xs font-medium text-muted-foreground">Ciudad / Municipio *</Label>
              <Input
                value={shipping.city}
                onChange={(e) => updateShipping('city', e.target.value)}
                placeholder="Se llena con el C.P."
                className="mt-1"
                name="shipping-city"
                autoComplete="shipping address-level2"
                data-testid="input-shipping-city"
              />
              <FieldError error={errors.shipping_city} />
            </div>
          </div>

          {/* Colonia dropdown or input */}
          <div>
            <Label className="text-xs font-medium text-muted-foreground">Colonia *</Label>
            {shippingColonias.length > 0 ? (
              <Select value={shipping.neighborhood} onValueChange={(v) => updateShipping('neighborhood', v)}>
                <SelectTrigger className="mt-1" data-testid="select-shipping-neighborhood">
                  <SelectValue placeholder="Selecciona tu colonia" />
                </SelectTrigger>
                <SelectContent>
                  {shippingColonias.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            ) : (
              <Input
                value={shipping.neighborhood}
                onChange={(e) => updateShipping('neighborhood', e.target.value)}
                placeholder="Ingresa tu C.P. para ver colonias"
                className="mt-1"
                name="shipping-neighborhood"
                autoComplete="shipping address-level3"
                data-testid="input-shipping-neighborhood"
              />
            )}
          </div>

          <div>
            <Label className="text-xs font-medium text-muted-foreground">Referencias de entrega (opcional)</Label>
            <Input
              value={shipping.references || ''}
              onChange={(e) => updateShipping('references', e.target.value)}
              placeholder="Entre calles, edificio, bodega, etc."
              className="mt-1"
              data-testid="input-shipping-references"
            />
          </div>

          <div>
            <Label className="text-xs font-medium text-muted-foreground">Indicaciones adicionales (opcional)</Label>
            <Input
              value={shipping.additional_instructions || ''}
              onChange={(e) => updateShipping('additional_instructions', e.target.value)}
              placeholder="Horario de recepción, instrucciones especiales, etc."
              className="mt-1"
              data-testid="input-shipping-instructions"
            />
          </div>
        </div>
      </section>

      {/* ==================== INVOICE (OPTIONAL) ==================== */}
      <section data-testid="section-invoice">
        <div className="flex items-center gap-2 pb-3 mb-4 border-b border-accent/20">
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={invoiceRequested}
              onChange={(e) => onInvoiceRequestedChange(e.target.checked)}
              className="w-5 h-5 rounded border-gray-300 text-accent"
              data-testid="checkbox-invoice-requested"
            />
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-md bg-accent/10 flex items-center justify-center">
                <FileText className="w-4 h-4 text-accent" />
              </div>
              <span className="font-bold text-primary text-sm tracking-wide uppercase">Necesito factura</span>
            </div>
          </label>
        </div>

        {invoiceRequested && (
          <div className="space-y-4 animate-in fade-in duration-200">
            <p className="text-xs text-muted-foreground">
              Completa tus datos fiscales. También puedes solicitar factura después de tu compra.
            </p>

            {/* Billing type */}
            <div>
              <Label className="text-xs font-medium text-muted-foreground">Tipo de facturación</Label>
              <RadioGroup
                value={billing.billing_type}
                onValueChange={(v) => updateBilling('billing_type', v)}
                className="flex gap-4 mt-2"
              >
                <div className="flex items-center gap-2">
                  <RadioGroupItem value="persona_fisica" id="pf" data-testid="radio-persona-fisica" />
                  <Label htmlFor="pf" className="cursor-pointer text-sm">Persona física</Label>
                </div>
                <div className="flex items-center gap-2">
                  <RadioGroupItem value="empresa" id="emp" data-testid="radio-empresa" />
                  <Label htmlFor="emp" className="cursor-pointer text-sm">Empresa</Label>
                </div>
              </RadioGroup>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs font-medium text-muted-foreground">Razón social *</Label>
                <Input
                  value={billing.razon_social || billing.name_or_company}
                  onChange={(e) => updateBilling('razon_social', e.target.value)}
                  placeholder={billing.billing_type === 'empresa' ? 'Razón social' : 'Nombre completo'}
                  className="mt-1"
                  data-testid="input-billing-razon-social"
                />
                <FieldError error={errors.billing_razon_social} />
              </div>
              <div>
                <Label className="text-xs font-medium text-muted-foreground">
                  RFC * ({billing.billing_type === 'persona_fisica' ? '13 caracteres' : '12 caracteres'})
                </Label>
                <Input
                  value={billing.rfc}
                  onChange={(e) => updateBilling('rfc', e.target.value.toUpperCase())}
                  placeholder="Ej: XAXX010101000"
                  className="mt-1 uppercase"
                  maxLength={billing.billing_type === 'persona_fisica' ? 13 : 12}
                  data-testid="input-billing-rfc"
                />
                <FieldError error={errors.billing_rfc} />
              </div>
            </div>

            <div>
              <Label className="text-xs font-medium text-muted-foreground">Correo para factura *</Label>
              <Input
                type="email"
                value={billing.invoice_email || billing.email}
                onChange={(e) => updateBilling('invoice_email', e.target.value)}
                placeholder="facturacion@ejemplo.com"
                className="mt-1"
                name="billing-email"
                autoComplete="email"
                data-testid="input-billing-invoice-email"
              />
              <FieldError error={errors.billing_invoice_email} />
            </div>

            <label className="flex items-center gap-3 p-3 rounded-lg border cursor-pointer hover:bg-muted/30 transition-colors">
              <input
                type="checkbox"
                checked={billing.fiscal_same_as_shipping || false}
                onChange={(e) => {
                  updateBilling('fiscal_same_as_shipping', e.target.checked);
                  if (e.target.checked) {
                    updateBilling('state', shipping.state);
                    updateBilling('street', shipping.street);
                    updateBilling('neighborhood', shipping.neighborhood);
                    updateBilling('city', shipping.city);
                    updateBilling('postal_code', shipping.postal_code);
                  }
                }}
                className="w-4 h-4 rounded border-gray-300 text-accent"
                data-testid="checkbox-fiscal-same"
              />
              <span className="text-sm font-medium">Usar la dirección de entrega como domicilio fiscal</span>
            </label>

            {!billing.fiscal_same_as_shipping && (
              <>
                <div>
                  <Label className="text-xs font-medium text-muted-foreground">Calle y número *</Label>
                  <Input
                    value={billing.street}
                    onChange={(e) => updateBilling('street', e.target.value)}
                    placeholder="Ej: Av. Reforma 123"
                    className="mt-1"
                    name="billing-street"
                    autoComplete="billing street-address"
                    data-testid="input-billing-street"
                  />
                  <FieldError error={errors.billing_street} />
                </div>

                <div className="grid sm:grid-cols-3 gap-4">
                  <div>
                    <Label className="text-xs font-medium text-muted-foreground">C.P. *</Label>
                    <div className="relative mt-1">
                      <Input
                        value={billing.postal_code}
                        onChange={(e) => handleBillingCpChange(e.target.value)}
                        placeholder="00000"
                        maxLength={5}
                        inputMode="numeric"
                        name="billing-postal-code"
                        autoComplete="billing postal-code"
                        data-testid="input-billing-postal"
                      />
                      {billingCpLoading && (
                        <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-muted-foreground" />
                      )}
                    </div>
                    <FieldError error={errors.billing_postal} />
                  </div>
                  <div>
                    <Label className="text-xs font-medium text-muted-foreground">Estado *</Label>
                    {billing.country === 'México' ? (
                      <Select value={billing.state} onValueChange={(v) => updateBilling('state', v)}>
                        <SelectTrigger className="mt-1" data-testid="select-billing-state">
                          <SelectValue placeholder="Selecciona un estado" />
                        </SelectTrigger>
                        <SelectContent>
                          {MEXICAN_STATES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    ) : (
                      <Input
                        value={billing.state}
                        onChange={(e) => updateBilling('state', e.target.value)}
                        placeholder="Estado o provincia"
                        className="mt-1"
                      />
                    )}
                    <FieldError error={errors.billing_state} />
                  </div>
                  <div>
                    <Label className="text-xs font-medium text-muted-foreground">Ciudad *</Label>
                    <Input
                      value={billing.city}
                      onChange={(e) => updateBilling('city', e.target.value)}
                      placeholder="Se llena con el C.P."
                      className="mt-1"
                      name="billing-city"
                      autoComplete="billing address-level2"
                      data-testid="input-billing-city"
                    />
                    <FieldError error={errors.billing_city} />
                  </div>
                </div>

                <div>
                  <Label className="text-xs font-medium text-muted-foreground">Colonia</Label>
                  {billingColonias.length > 0 ? (
                    <Select value={billing.neighborhood} onValueChange={(v) => updateBilling('neighborhood', v)}>
                      <SelectTrigger className="mt-1" data-testid="select-billing-neighborhood">
                        <SelectValue placeholder="Selecciona tu colonia" />
                      </SelectTrigger>
                      <SelectContent>
                        {billingColonias.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Input
                      value={billing.neighborhood}
                      onChange={(e) => updateBilling('neighborhood', e.target.value)}
                      placeholder="Ingresa tu C.P. para ver colonias"
                      className="mt-1"
                      name="billing-neighborhood"
                      autoComplete="billing address-level3"
                      data-testid="input-billing-neighborhood"
                    />
                  )}
                </div>
              </>
            )}

            <div>
              <Label className="text-xs font-medium text-muted-foreground">Uso de CFDI (opcional)</Label>
              <Select value={billing.cfdi_use || ''} onValueChange={(v) => updateBilling('cfdi_use', v)}>
                <SelectTrigger className="mt-1" data-testid="select-cfdi">
                  <SelectValue placeholder="Selecciona (opcional)" />
                </SelectTrigger>
                <SelectContent>
                  {CFDI_USES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
        )}
      </section>
    </div>
  );
};

export default CustomerForm;
